import type { Color, NodeRecord, Paint, Scene, TextRun } from '@flobi/artwork-core';
import { colorToHex } from '../theme';

/**
 * Selection colours, Figma's way: every solid colour worn inside what is chosen — fills,
 * outlines and text, down through groups and artboards — listed once each, so one change
 * recolours every layer that wears it.
 *
 * A colour is its channels and its alpha; the same red at 50% is a second colour. Hidden
 * paints and locked layers are left out: what cannot be seen or changed is not offered.
 */

/** Where one colour sits: a paint in a list, on the node, its text style or one of its runs. */
export type ColorPlace =
  | { id: string; list: 'fills' | 'strokes'; index: number }
  | { id: string; list: 'text'; index: number }
  | { id: string; list: 'run'; run: number; index: number };

export interface SelectionColor {
  key: string;
  color: Color;
  places: ColorPlace[];
}

export const keyOf = (color: Color) => `${colorToHex(color)}/${Math.round((color.a ?? 1) * 100)}`;

type Worn = NodeRecord & { fills?: Paint[]; strokes?: Paint[]; style?: { fills?: Paint[] }; runs?: TextRun[] };

/** The colours inside these layers, in the order they are painted. */
export function selectionColors(scene: Scene, ids: readonly string[]): SelectionColor[] {
  const found = new Map<string, SelectionColor>();
  const seen = new Set<string>();
  const add = (color: Color, place: ColorPlace) => {
    const key = keyOf(color);
    const entry = found.get(key);
    if (entry) entry.places.push(place);
    else found.set(key, { key, color, places: [place] });
  };
  const each = (paints: readonly Paint[] | undefined, at: (index: number) => ColorPlace) =>
    paints?.forEach((paint, index) => { if (paint.type === 'solid' && paint.visible !== false) add(paint.color, at(index)); });
  for (const root of ids) {
    for (const id of scene.subtree(root)) {
      if (seen.has(id) || scene.lockedHere(id)) continue;
      seen.add(id);
      const node = scene.get(id) as Worn | undefined;
      if (!node) continue;
      if (node.type === 'text') {
        each(node.style?.fills, (index) => ({ id, list: 'text', index }));
        node.runs?.forEach((run, at) => each(run.style?.fills, (index) => ({ id, list: 'run', run: at, index })));
      } else {
        each(node.fills, (index) => ({ id, list: 'fills', index }));
      }
      if ((node.strokes?.length ?? 0) > 0) each(node.strokes, (index) => ({ id, list: 'strokes', index }));
    }
  }
  return [...found.values()];
}

/**
 * The writes that put `to` in every one of these places, one per layer. The paint keeps
 * everything but its colour; a run keeps its text and the rest of its style.
 */
export function recolorWrites(scene: Scene, places: readonly ColorPlace[], to: Color): { id: string; props: Record<string, unknown> }[] {
  const byNode = new Map<string, ColorPlace[]>();
  for (const place of places) {
    const list = byNode.get(place.id);
    if (list) list.push(place);
    else byNode.set(place.id, [place]);
  }
  const paint = (paints: readonly Paint[], index: number): Paint[] =>
    paints.map((p, i) => (i === index && p.type === 'solid' ? { ...p, color: { ...to } } : p));
  const writes: { id: string; props: Record<string, unknown> }[] = [];
  for (const [id, at] of byNode) {
    const node = scene.get(id) as Worn | undefined;
    if (!node) continue;
    const props: Record<string, unknown> = {};
    let fills = node.fills, strokes = node.strokes, style = node.style, runs = node.runs;
    for (const place of at) {
      if (place.list === 'fills' && fills) fills = paint(fills, place.index);
      else if (place.list === 'strokes' && strokes) strokes = paint(strokes, place.index);
      else if (place.list === 'text' && style?.fills) style = { ...style, fills: paint(style.fills, place.index) };
      else if (place.list === 'run' && runs?.[place.run]?.style?.fills) {
        runs = runs.map((run, i) => (i === place.run ? { ...run, style: { ...run.style, fills: paint(run.style.fills!, place.index) } } : run));
      }
    }
    if (fills !== node.fills) props.fills = fills;
    if (strokes !== node.strokes) props.strokes = strokes;
    if (style !== node.style) props.style = style;
    if (runs !== node.runs) props.runs = runs;
    if (Object.keys(props).length) writes.push({ id, props });
  }
  return writes;
}
